import * as React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Download, EyeOff, FileSearch, Filter, Play, Search, X } from 'lucide-react';
import { useSessao } from '@/hooks/useSessao';
import { NavInventario } from '@/componentes/inventario/NavInventario';
import {
  useConsultasInventario,
  useExecutarConsulta,
  useInventario,
  type CampoConsulta,
  type DefinicaoConsulta,
} from '@/hooks/inventario';
import { ErroHttp } from '@/lib/api';
import { baixarCsv, carimboArquivo, type ColunaCsv } from '@/lib/csv';
import { cn, dataCurta, dataHora, numero } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';
import { CabecalhoPagina, Cartao, Esqueleto } from '@/componentes/ui/primitivos';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import {
  GradeRelatorio,
  aplicarFiltros,
  type ColunaGrade,
  type FiltrosGrade,
} from '@/componentes/tabela/GradeRelatorio';

/**
 * Uma consulta do menu Estoque > Inventário do PBS, em tela.
 *
 * A consulta é a do PBS — SQL, parâmetros, títulos das colunas e o que
 * totaliza vêm do dicionário, ao vivo. O que muda é o uso: os parâmetros já
 * chegam preenchidos com os escopos deste inventário, a grade ordena por
 * coluna e filtra enquanto digita, os totais ficam fixos e o Excel sai com
 * EAN e códigos como texto.
 */

type Linha = Record<string, unknown>;

/** Colunas que são identificador: saem como texto no Excel e alinhadas à esquerda. */
const IDENTIFICADORES = /^(EAN|PRODUTO|COD_LOCALIZADOR|CODIGO_LOCALIZADOR|INVENTARIO_ESCOPO|OBJETO_CONTROLE|CENTRO_ESTOQUE)$/;

/**
 * Valor por unidade: somar não quer dizer nada (custo, preço, médio,
 * percentual). CUSTO_TOTAL, VALOR_TOTAL e afins somam normalmente.
 */
const POR_UNIDADE = /^(CUSTO|PRECO|ULTIMO_CUSTO|CUSTO_MEDIO|PRECO_MEDIO|PRECO_FABRICA|PRECO_VENDA)$|UNITARIO|PERC|ALIQUOTA|MARGEM/;

function ehNumero(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function formatar(v: unknown, campo: CampoConsulta | undefined, chave: string): string {
  if (v === null || v === undefined) return '';
  if (v instanceof Date) return dataHora(v.toISOString());
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) return dataHora(v);
  if (ehNumero(v)) {
    if (IDENTIFICADORES.test(chave)) return String(v);
    const casas = campo?.decimais ?? (Number.isInteger(v) ? 0 : 2);
    return v.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });
  }
  return String(v).trim();
}

/** Trocar de consulta pela barra remonta a página: cada uma tem seus filtros. */
export function InventarioConsulta(): JSX.Element {
  const { consulta } = useParams();
  return <PaginaConsulta key={consulta ?? ''} />;
}

function PaginaConsulta(): JSX.Element {
  const { id: bruto, consulta: chave } = useParams();
  const { temPermissao } = useSessao();
  const id = Number(bruto);
  const [url] = useSearchParams();
  const detalhe = useInventario(Number.isInteger(id) && id > 0 ? id : null);
  const definicoes = useConsultasInventario();
  const executar = useExecutarConsulta();

  const def = definicoes.data?.consultas.find((d) => d.consulta === chave) ?? null;
  const escopos = React.useMemo(
    () => (detalhe.data?.escopos ?? []).map((e) => e.escopo).sort((a, b) => a - b),
    [detalhe.data],
  );

  const [valores, definirValores] = React.useState<Record<string, string> | null>(null);
  const [busca, definirBusca] = React.useState('');

  // Valores iniciais: o que veio na URL, senão os escopos deste inventário.
  React.useEffect(() => {
    if (!def || !detalhe.data || valores) return;
    const escopoUrl = url.get('escopo');
    const primeiro = escopos[0];
    const ultimo = escopos[escopos.length - 1];
    const v: Record<string, string> = {};
    for (const p of def.parametros) {
      if (p.tipo === 'escopo') v[p.parametro] = escopoUrl ?? (ultimo ? String(ultimo) : '');
      // Faixa: do primeiro ao último escopo do inventário. A numeração é única
      // para todos os centros e a faixa pode pegar escopo de fora no meio —
      // a tela avisa quais (ver `foraDoInventario`).
      else if (p.tipo === 'escopo_ini') v[p.parametro] = escopoUrl ?? (primeiro ? String(primeiro) : '');
      else if (p.tipo === 'escopo_fim') v[p.parametro] = escopoUrl ?? (ultimo ? String(ultimo) : '');
      else v[p.parametro] = ehSimNao(p.titulo) ? 'S' : '';
    }
    definirValores(v);
    // Com os escopos preenchidos, já roda: quem abriu quer ver o resultado.
    if (def.parametros.every((p) => p.tipo === 'texto' || (v[p.parametro] ?? '') !== '')) {
      executar.mutate({ consulta: def.consulta, valores: v });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [def, detalhe.data]);

  if (definicoes.isError) return <EstadoErro erro={definicoes.error} />;
  if (detalhe.isError) return <EstadoErro erro={detalhe.error} />;
  if (definicoes.isLoading || detalhe.isLoading || !valores) {
    if (definicoes.data && !def) {
      return <EstadoVazio titulo="Consulta não encontrada" detalhe="Ela não está no menu de Inventário do PBS." />;
    }
    return (
      <div className="space-y-3">
        <Esqueleto className="h-16 w-full" />
        <Esqueleto className="h-24 w-full" />
        <Esqueleto className="h-96 w-full" />
      </div>
    );
  }
  if (!def) return <EstadoVazio titulo="Consulta não encontrada" />;

  const rodar = (): void => executar.mutate({ consulta: def.consulta, valores });
  // Lista cega: a própria consulta do PBS tem "Exibir saldo" e "Exibir
  // divergência" (S/N). Com os dois em Não, sai a lista para recontar sem o
  // número esperado.
  const pCegos = def.parametros.filter((p) => /^EXIBE?(SALDO|DIVERGENCIA)$|^EXIBIR(SALDO|DIVERGENCIA)$/.test(p.parametro));
  const cega = pCegos.length > 0 && pCegos.every((p) => valores[p.parametro] === 'N');
  const rodarCega = (): void => {
    const v = { ...valores };
    for (const p of pCegos) v[p.parametro] = 'N';
    definirValores(v);
    executar.mutate({ consulta: def.consulta, valores: v });
  };
  // O escopo que a barra leva para a próxima consulta.
  const pEscopo = def.parametros.find((p) => p.tipo === 'escopo' || p.tipo === 'escopo_ini');
  const escopoEmUso = pEscopo ? Number(valores[pEscopo.parametro]) || null : null;
  // Escopos da faixa que não são deste inventário (a numeração é de todos os centros).
  const foraDoInventario = (executar.data?.escoposNaFaixa ?? []).filter((e) => !escopos.includes(e));

  return (
    <div className="space-y-4 p-3 sm:p-5 sm:pl-8">
      <NavInventario
        inventario={id}
        nome={detalhe.data?.inventario.nome ?? 'Inventário'}
        escopo={escopoEmUso}
        podePlanejar={temPermissao('inventario.planejar')}
      />

      <CabecalhoPagina
        sobrelinha="Consulta do PBS · Estoque > Inventário"
        titulo={def.descricao}
        descricao={
          <>
            Mesma consulta do PBS ({def.consulta}
            {def.ultimaAlteracao ? `, alterada em ${dataCurta(def.ultimaAlteracao)}` : ''}), lida ao vivo do
            dicionário.
          </>
        }
        icone={<FileSearch className="h-5 w-5" />}
      />

      <Cartao className="p-4">
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            rodar();
          }}
        >
          {def.parametros.map((p) => (
            <CampoParametro
              key={p.parametro}
              titulo={p.titulo}
              tipo={p.tipo}
              valor={valores[p.parametro] ?? ''}
              escopos={escopos}
              aoMudar={(x) => definirValores({ ...valores, [p.parametro]: x })}
            />
          ))}
          <Botao type="submit" variante="primaria" carregando={executar.isPending}>
            <Play className="h-4 w-4" />
            Consultar
          </Botao>
          {pCegos.length > 0 ? (
            <Botao
              type="button"
              variante={cega ? 'secundaria' : 'fantasma'}
              onClick={rodarCega}
              title="Esconde saldo e divergência: a lista para a recontagem, sem o número esperado"
            >
              <EyeOff className="h-4 w-4" />
              Lista cega
            </Botao>
          ) : null}
        </form>
      </Cartao>

      {executar.isError ? (
        <p className="rounded-lg bg-status-divergenciaFundo px-4 py-3 text-sm text-status-divergencia">
          {executar.error instanceof ErroHttp ? executar.error.message : 'Não foi possível rodar a consulta.'}
        </p>
      ) : null}

      {foraDoInventario.length > 0 ? (
        <p className="rounded-lg border border-status-atencao/40 bg-status-atencaoFundo px-4 py-3 text-sm">
          <b>A faixa pega {foraDoInventario.length === 1 ? 'um escopo que não é' : `${numero(foraDoInventario.length)} escopos que não são`} deste
          inventário:</b> {foraDoInventario.join(', ')}. O resultado soma {foraDoInventario.length === 1 ? 'ele' : 'eles'} junto.
        </p>
      ) : null}

      {executar.data ? (
        <Resultado def={def} resultado={executar.data} busca={busca} definirBusca={definirBusca} valores={valores} />
      ) : executar.isPending ? (
        <Esqueleto className="h-96 w-full" />
      ) : null}
    </div>
  );
}

function ehSimNao(titulo: string): boolean {
  return /\(S\/N\)/i.test(titulo);
}

function CampoParametro({
  titulo,
  tipo,
  valor,
  escopos,
  aoMudar,
}: {
  titulo: string;
  tipo: DefinicaoConsulta['parametros'][number]['tipo'];
  valor: string;
  escopos: number[];
  aoMudar: (v: string) => void;
}): JSX.Element {
  if (tipo === 'texto' && ehSimNao(titulo)) {
    return (
      <div>
        <span className="rotulo mb-1 block">{titulo.replace(/\s*\(S\/N\)\s*\??/i, '')}</span>
        <div className="flex rounded-lg border border-borda bg-superficie2 p-0.5">
          {(['S', 'N'] as const).map((o) => (
            <button
              key={o}
              type="button"
              onClick={() => aoMudar(o)}
              className={cn(
                'min-h-[38px] rounded-md px-4 text-sm font-medium',
                valor === o ? 'bg-superficie text-texto shadow-xs ring-1 ring-borda' : 'text-textoFraco',
              )}
            >
              {o === 'S' ? 'Sim' : 'Não'}
            </button>
          ))}
        </div>
      </div>
    );
  }
  const listaId = `escopos-${titulo.replace(/\W/g, '')}`;
  return (
    <label>
      <span className="rotulo mb-1 block">{titulo}</span>
      <input
        value={valor}
        onChange={(e) => aoMudar(tipo === 'texto' ? e.target.value : e.target.value.replace(/\D/g, ''))}
        list={tipo !== 'texto' ? listaId : undefined}
        inputMode={tipo !== 'texto' ? 'numeric' : undefined}
        className="min-h-toque w-40 rounded-lg border border-borda bg-superficie px-3 text-sm tabular focus:border-primaria focus:outline-none"
      />
      {tipo !== 'texto' ? (
        <datalist id={listaId}>
          {escopos.map((e) => (
            <option key={e} value={String(e)} />
          ))}
        </datalist>
      ) : null}
    </label>
  );
}

function Resultado({
  def,
  resultado,
  busca,
  definirBusca,
  valores,
}: {
  def: DefinicaoConsulta;
  resultado: { colunas: string[]; linhas: Linha[]; truncado: boolean; ms: number };
  busca: string;
  definirBusca: (v: string) => void;
  valores: Record<string, string>;
}): JSX.Element {
  // Colunas: as configuradas no PBS, na ordem dele; sem configuração, todas.
  const porCampo = new Map(def.campos.map((c) => [c.campo, c]));
  const chaves =
    def.campos.length > 0
      ? def.campos.map((c) => c.campo).filter((c) => resultado.colunas.includes(c))
      : resultado.colunas;

  // Índice estável por linha: duas linhas iguais não podem ter a mesma chave.
  const todas = React.useMemo(
    () => resultado.linhas.map((l, i): Linha => ({ ...l, __i: i })),
    [resultado.linhas],
  );
  const termo = busca.trim().toUpperCase();
  const buscadas = termo
    ? todas.filter((l) => chaves.some((k) => String(l[k] ?? '').toUpperCase().includes(termo)))
    : todas;
  // Filtros por coluna, estilo Excel. Totais e Excel usam o mesmo recorte.
  const [filtros, definirFiltros] = React.useState<FiltrosGrade>({});
  const filtrar = (chave: string, valores: string[] | null): void =>
    definirFiltros((atual) => {
      const novo = { ...atual };
      if (valores === null) delete novo[chave];
      else novo[chave] = valores;
      return novo;
    });

  const colunas: ColunaGrade<Linha>[] = chaves.map((k) => {
    const campo = porCampo.get(k);
    const numericaPorAmostra = resultado.linhas.slice(0, 50).some((l) => ehNumero(l[k]));
    const ident = IDENTIFICADORES.test(k);
    const elastica = /DESCRICAO|DESCR_|LOCALIZADOR$|MARCA/.test(k) && !ident;
    return {
      chave: k,
      cabecalho: campo?.titulo ?? k,
      largura: elastica ? 220 : ident ? 120 : 110,
      elastica,
      ...(numericaPorAmostra && !ident ? { alinhar: 'dir' as const } : {}),
      render: (l) => (
        <span className={cn(ident && 'font-mono', 'truncate')} title={formatar(l[k], campo, k)}>
          {formatar(l[k], campo, k)}
        </span>
      ),
      ordenar: (l) => {
        const v = l[k];
        return ehNumero(v) ? v : String(v ?? '');
      },
      filtrar: (l) => formatar(l[k], campo, k),
    };
  });
  const linhas = aplicarFiltros(buscadas, colunas, filtros);
  const ativos = Object.entries(filtros);

  /*
   * Rodapé, sobre o recorte da tela (busca e filtros), escolhido pelo campo:
   *   totalizar no PBS ou quantidade/valor  → soma;
   *   valor por unidade (custo, preço, %)   → nada (somar não quer dizer nada);
   *   identificador (produto, EAN, escopo)  → quantos distintos;
   *   1ª coluna de texto                    → quantas linhas.
   */
  const primeiraTexto = colunas.find(
    (c) => c.alinhar !== 'dir' && !IDENTIFICADORES.test(c.chave) && c.chave !== 'LOCALIZADOR',
  )?.chave;
  const comRodape: ColunaGrade<Linha>[] = colunas.map((c) => {
    const campo = porCampo.get(c.chave);
    const k = c.chave;
    if (IDENTIFICADORES.test(k) || k === 'LOCALIZADOR') {
      const n = new Set(linhas.map((l) => l[k]).filter((v) => v !== null && v !== undefined && v !== '')).size;
      return { ...c, rodape: <span className="font-semibold text-textoFraco">{numero(n)} distintos</span> };
    }
    if (c.alinhar === 'dir' && (campo?.totalizar || !POR_UNIDADE.test(k))) {
      const soma = linhas.reduce((s, l) => s + (ehNumero(l[k]) ? (l[k] as number) : 0), 0);
      return {
        ...c,
        rodape: (
          <span className={cn(soma < 0 && 'text-status-divergencia')} title={`Soma de ${c.cabecalho}`}>
            {formatar(soma, campo, k)}
          </span>
        ),
      };
    }
    if (k === primeiraTexto) {
      return { ...c, rodape: <span className="text-textoFraco">{numero(linhas.length)} linhas</span> };
    }
    return { ...c, rodape: null };
  });


  const csv: ColunaCsv<Linha>[] = chaves.map((k) => {
    const campo = porCampo.get(k);
    return {
      cabecalho: campo?.titulo ?? k,
      valor: (l) => {
        const v = l[k];
        if (v === null || v === undefined) return '';
        if (ehNumero(v) && !IDENTIFICADORES.test(k)) return v.toLocaleString('pt-BR', { maximumFractionDigits: 6, useGrouping: false });
        return formatar(v, campo, k);
      },
      ...(IDENTIFICADORES.test(k) ? { texto: true } : {}),
    };
  });

  const sufixo = Object.values(valores).filter(Boolean).join('-');

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <label className="relative w-full max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-textoSuave" />
          <input
            value={busca}
            onChange={(e) => definirBusca(e.target.value)}
            placeholder="Filtrar nas colunas (produto, EAN, endereço…)"
            className="min-h-toque w-full rounded-lg border border-borda bg-superficie pl-9 pr-3 text-sm focus:border-primaria focus:outline-none"
          />
        </label>
        <span className="text-xs text-textoFraco">
          {numero(linhas.length)}
          {linhas.length !== resultado.linhas.length ? ` de ${numero(resultado.linhas.length)}` : ''} linhas ·{' '}
          {numero(resultado.ms)} ms
          {resultado.truncado ? ' · mostrando as primeiras 50.000' : ''}
        </span>
        <Botao
          variante="secundaria"
          tamanho="pequeno"
          className="ml-auto"
          disabled={linhas.length === 0}
          onClick={() => baixarCsv(`${def.consulta.toLowerCase()}-${sufixo}-${carimboArquivo()}`, linhas, csv)}
        >
          <Download className="h-4 w-4" />
          Excel
        </Botao>
      </div>

      {ativos.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <Filter className="h-3.5 w-3.5 text-primaria" />
          {ativos.map(([k, v]) => (
            <span
              key={k}
              className="inline-flex max-w-[320px] items-center gap-1 rounded-full bg-primaria-suave py-0.5 pl-2.5 pr-1 text-xs text-primaria"
            >
              <b className="shrink-0">{porCampo.get(k)?.titulo ?? k}:</b>
              <span className="truncate">{v.length <= 3 ? v.map((x) => x || '(vazio)').join(', ') : `${numero(v.length)} valores`}</span>
              <button
                type="button"
                aria-label={`Tirar o filtro de ${porCampo.get(k)?.titulo ?? k}`}
                onClick={() => filtrar(k, null)}
                className="grid h-5 w-5 place-items-center rounded-full hover:bg-primaria/15"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <button type="button" onClick={() => definirFiltros({})} className="text-xs text-textoFraco underline">
            Limpar filtros
          </button>
        </div>
      ) : null}

      {resultado.linhas.length === 0 ? (
        <EstadoVazio titulo="A consulta não trouxe linhas" detalhe="Com esses parâmetros o PBS também não traria." />
      ) : (
        <GradeRelatorio
          preencherTela
          linhas={linhas}
          base={buscadas}
          colunas={comRodape}
          filtros={filtros}
          aoFiltrar={filtrar}
          chaveDe={(l) => String(l.__i)}
        />
      )}
    </div>
  );
}
